<template>
  <div class="col-12">
    <div class="row">
      <!-- PANE HEADER -->
      <div class="col-12">
        <div class="pane-header">
          <div class="flex-h-full">
            <span class="icon flex mr-2"><icon-colored-co2 /></span>
            <span>{{ capitalize($t('messages.sustainability')) }}</span>
            <span style="flex-basis: 100%"></span>
            <span
              class="badge badge-sm badge-disabled"
              style="margin-bottom: 0">
              {{ $t('messages.poweredByScope3') }}
            </span>
          </div>
        </div>
      </div>
      <!-- PANE CONTENT -->
      <div class="col-12">
        <div class="pane-content">
          <div v-if="deal.co2EmissionsScore > 0">
            <img
              class="scope3-logo"
              src="/assets/images/scope3.svg"
              alt="Scope3" />

            <!-- COMPARATIVE RANKING -->
            <div class="row">
              <div class="col-12">
                <h4>{{ capitalize($t('messages.comparativeRanking')) }}</h4>
                <span class="text-muted">{{ $t('messages.comparativeRankingDescription') }}</span>
              </div>
            </div>
            <div class="pt-3 pl-3 pr-5 flex-h-full justify-content-center">
              <green-score
                class="m-3"
                style="width: 7rem; height: 7rem; font-size: 1.6rem; flex-shrink: 0"
                :show-green-leaf="false"
                :co2-score="deal.co2EmissionsScore" />
              <green-score
                v-if="deal.co2EmissionsScore >= 50"
                class="m-3"
                style="width: 7rem; height: 7rem; font-size: 1.4rem; flex-shrink: 0"
                :is-animating="false"
                :co2-score="deal.co2EmissionsScore" />
              <progress-bar
                class="ml-3"
                :full-threshold="80"
                :max="100"
                :use-gradient="true"
                :value="deal.co2EmissionsScore"
                :marks="[{ value: 80, icon: markRaw(IconColoredLeaf) }]"
                :start-label="capitalize(i18n.global.t('messages.worse'))"
                :end-label="capitalize(i18n.global.t('messages.better'))"></progress-bar>
            </div>
            <hr v-if="showEmissionsBreakdown && showEmissionsByVenueType" />
            <div class="row">
              <div
                class="col-12"
                :class="{ 'col-xl-6': showEmissionsBreakdown && showEmissionsByVenueType }">
                <!-- EMISSIONS BREAKDOWN -->
                <template v-if="showEmissionsBreakdown">
                  <hr v-if="!showEmissionsBreakdown || !showEmissionsByVenueType" />
                  <h4>{{ capitalize($t('messages.emissionsBreakdown')) }}</h4>
                  <span class="text-muted">{{ $t('messages.whereDoTheEmissionsComeFrom') }}</span>
                  <div class="p-3">
                    <chart
                      margin-top="0"
                      :echarts-options="emissionsBreakdownChartOptions"
                      :name="i18n.global.t('messages.emissionsBreakdown')"
                      :theme="ChartTheme.THREE_TONES"></chart>
                  </div>
                </template>
              </div>
              <div
                class="col-12"
                :class="{
                  'col-xl-6': showEmissionsBreakdown && showEmissionsByVenueType,
                  'vertical-separator': showEmissionsBreakdown && showEmissionsByVenueType,
                }">
                <!-- VENUE TYPE -->
                <template v-if="showEmissionsByVenueType">
                  <hr v-if="!showEmissionsBreakdown || !showEmissionsByVenueType" />
                  <h4>{{ capitalize($t('names.venueType', { n: 1 })) }}</h4>
                  <span class="text-muted">{{ $t('messages.compareEmissionsByVenueType') }}</span>
                  <div class="p-3">
                    <chart
                      margin-top="0"
                      :mode="ChartMode.NONE"
                      :echarts-options="breakdownByVenueTypeChartOptions"
                      :name="i18n.global.t('names.venueType')"
                      :theme="ChartTheme.TEN_TONES"></chart>
                  </div>
                </template>
              </div>
              <!-- BENCHMARKS -->
              <div class="col-12">
                <hr />
                <h4>{{ capitalize($t('names.benchmark', { n: 2 })) }}</h4>
                <span class="text-muted">{{ $t('messages.sustainabilityBenchmarksDescription') }}</span>
                <div class="pt-5 px-3">
                  <div class="benchmarks-container">
                    <div class="search-and-filters"></div>
                  </div>
                  <div class="well">{{ capitalize($t('messages.comingSoon')) }}</div>
                </div>
              </div>
            </div>
          </div>

          <div v-else>
            <div class="no-measurements">
              <icon-complex-sad></icon-complex-sad>
              <span>{{ $t('messages.noMeasurementAvailable') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, markRaw, onMounted, ref } from 'vue';

  import Chart from '@/common/components/chart/Chart.vue';
  import GreenScore from '@/common/components/controls/GreenScore.vue';
  import ProgressBar from '@/common/components/controls/ProgressBar.vue';
  import ChartMode from '@/common/enums/chart-mode.enum';
  import ChartTheme from '@/common/enums/chart-theme.enum';
  import { IconColoredCo2, IconColoredLeaf } from '@/common/icons/colored';
  import { IconComplexSad } from '@/common/icons/complex';
  import { Package } from '@/common/models';
  import CurateUnitCo2eEmissionsByVenueType from '@/common/models/deliveryStatistics/CurateUnitCo2eEmissionsByVenueType';
  import { formatNumber } from '@/globals';
  import { useMediaTypes } from '@/org-app/services/tanstack/queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import { mapActions } from '@/utils/StateMapping';
  import i18n from '@/vueI18n';

  interface IDealDetailsGreenScore {
    deal: Package;
  }

  const { getCurateUnitCo2eEmissionsByVenueType } = mapActions();

  const props = defineProps<IDealDetailsGreenScore>();

  const emissionsByVenueType = ref<CurateUnitCo2eEmissionsByVenueType[]>(null);

  const showEmissionsBreakdown = computed(() => {
    return props.deal.co2Emissions?.totalGco2e > 0;
  });

  const showEmissionsByVenueType = computed(() => {
    return (
      emissionsByVenueType.value?.length > 0 && emissionsByVenueType.value.reduce((acc, x) => acc + x.totalGco2e, 0) > 0
    );
  });

  const series = computed(() => {
    if (emissionsByVenueType.value == null) return [];

    const s = emissionsByVenueType.value
      ?.filter((x) => x.mediaTypeName != null)
      .sort((a, b) => b.totalGco2e - a.totalGco2e)
      .map((x) => ({
        type: 'bar',
        name: x.mediaTypeName,
        data: [x.totalGco2e],
      }));

    const maxSeries = 10;
    if (s.length > maxSeries) {
      const sum = s.slice(maxSeries).reduce((acc, x) => {
        acc += x.data[0];
        return acc;
      }, 0);
      return [
        ...s.slice(0, maxSeries - 1),
        {
          type: 'bar',
          name: capitalize(i18n.global.t('names.other')),
          data: [sum / (s.length - (maxSeries - 1))],
        },
      ];
    }
    return s;
  });

  const emissionsBreakdownChartOptions = computed(() => ({
    calculable: true,
    series: {
      type: 'pie',
      radius: '65%',
      center: ['50%', '50%'],
      labelLine: {
        length: 30,
      },
      label: {
        formatter: (params) =>
          `{b|${params['name']}：}${formatNumber(params.value, '0.0a')} {u|CO2e(g)} {per|${params.percent}%}`,
        backgroundColor: '#fcfcfc',
        borderColor: '#ddd',
        borderWidth: 0.5,
        borderRadius: 4,
        fontSize: 11,
        padding: [4, 8],
        rich: {
          b: {
            color: '#4C5058',
            fontSize: 11,
            fontWeight: 'bold',
          },
          per: {
            color: '#fff',
            fontSize: 11,
            backgroundColor: '#4C5058',
            padding: [3, 5],
            borderRadius: 3,
          },
          u: {
            color: '#777',
            fontSize: 9,
            // fontStyle: 'italic',
          },
        },
      },
      data: [
        ...(props.deal.co2Emissions.adSelectionGco2e > 0
          ? [
              {
                name: capitalize(i18n.global.t('messages.adSelection')),
                value: props.deal.co2Emissions.adSelectionGco2e,
              },
            ]
          : []),
        ...(props.deal.co2Emissions.creativeDeliveryGco2e > 0
          ? [
              {
                name: capitalize(i18n.global.t('messages.creativeDelivery')),
                value: props.deal.co2Emissions.creativeDeliveryGco2e,
              },
            ]
          : []),
        ...(props.deal.co2Emissions.mediaDistributionGco2e > 0
          ? [
              {
                name: capitalize(i18n.global.t('messages.mediaDistribution')),
                value: props.deal.co2Emissions.mediaDistributionGco2e,
              },
            ]
          : []),
      ],
    },
    tooltip: {
      trigger: 'item',
      axisPointer: {
        type: 'shadow',
      },
      formatter: 'CO2e (g): <b>{c}</b>',
    },
  }));

  const breakdownByVenueTypeChartOptions = computed(() => ({
    grid: {
      top: 30,
      left: 60,
      right: 20,
      bottom: series.value.length > 5 ? 90 : 50,
    },
    legend: {
      bottom: 0,
      data: series.value.map((x) => x.name),
      textStyle: {
        fontSize: 10,
      },
    },
    calculable: true,
    series: series.value,
    xAxis: {
      type: 'category',
      axisLabel: {
        show: false,
      },
    },
    yAxis: {
      name: 'CO2e (g)',
      type: 'value',
      nameLocation: 'middle',
      axisLabel: {
        fontSize: 10,
      },
      nameGap: 32,
      nameTextStyle: {
        fontSize: 8,
      },
    },
    tooltip: {
      trigger: 'item',
      axisPointer: {
        type: 'shadow',
      },
      formatter: 'CO2e (g): <b>{c}</b>',
    },
  }));

  onMounted(async () => {
    emissionsByVenueType.value = await getCurateUnitCo2eEmissionsByVenueType({ params: props.deal.targetParams });
    waitForPendingQueries([useMediaTypes()]);
  });
</script>

<style lang="scss">
  @use '@/styles/variables' as *;
  .no-measurements {
    display: flex;
    align-items: center;
    justify-content: center;
    padding-top: 1.6rem;

    svg {
      display: block;
      width: 8.2rem;
      height: 8.2rem;
      margin-right: 2.4rem;
    }
  }

  .vertical-separator {
    border-left: solid 0.1rem $borders-light;
  }

  .scope3-logo {
    position: absolute;
    right: 3rem;
    top: 1.6rem;
    height: 2rem;
  }
</style>
